import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import StudentList from '../components/StudentList';
import StudentForm from '../components/StudentForm';
import { supabase } from '../supabase/client';
import { useAuth } from '../contexts/AuthContext';
import { uploadImage, deleteImage } from '../utils/imageUpload';

const AdminDashboard = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.from('students').select('*').order('created_at', { ascending: false });

      if (error) throw error;
      setStudents(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = () => {
    setSelectedStudent(null);
    setFormOpen(true);
  };

  const handleEditStudent = (student) => {
    setSelectedStudent(student);
    setFormOpen(true);
  };

  const handleDeleteClick = (studentId) => {
    setStudentToDelete(studentId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const student = students.find((s) => s.id === studentToDelete);

      // Delete image from storage if it's hosted on Supabase
      if (student?.picture_url && student.picture_url.includes('supabase')) {
        await deleteImage(student.picture_url);
      }

      const { error } = await supabase.from('students').delete().eq('id', studentToDelete);

      if (error) throw error;
      await fetchStudents();
      setDeleteDialogOpen(false);
      setStudentToDelete(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFormSubmit = async (formData, file) => {
    try {
      setUploading(true);
      setError(null);

      let pictureUrl = formData.picture_url;

      // If a file was selected, upload it first
      if (file) {
        const { url, error: uploadError } = await uploadImage(file);

        if (uploadError) {
          throw uploadError;
        }

        pictureUrl = url;
      }

      // Prepare student data
      const studentData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        picture_url: pictureUrl,
        achievements: formData.achievements,
        achievement_type: formData.achievement_type,
      };

      if (selectedStudent) {
        // If updating and new image is uploaded, delete old image
        if (file && selectedStudent.picture_url && selectedStudent.picture_url.includes('supabase')) {
          await deleteImage(selectedStudent.picture_url);
        }

        const { error } = await supabase.from('students').update(studentData).eq('id', selectedStudent.id);

        if (error) throw error;
      } else {
        const { error } = await supabase.from('students').insert([studentData]);

        if (error) throw error;
      }

      await fetchStudents();
      setFormOpen(false);
      setSelectedStudent(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='100vh'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
        <Typography variant='h3' component='h1'>
          Удирдлагын хэсэг
        </Typography>
        <Box display='flex' gap={2}>
          <Button variant='contained' color='primary' startIcon={<AddIcon />} onClick={handleAddStudent}>
            Сурагч нэмэх
          </Button>
          <Button variant='outlined' color='error' startIcon={<LogoutIcon />} onClick={handleLogout}>
            Гарах
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {students.length === 0 ? (
        <Typography align='center' color='text.secondary' sx={{ mt: 4 }}>
          Сурагч олдсонгүй. "Сурагч нэмэх" дарж шинэ Сурагч үүсгэнэ үү.
        </Typography>
      ) : (
        <StudentList students={students} onEdit={handleEditStudent} onDelete={handleDeleteClick} isAdmin={true} />
      )}

      <StudentForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelectedStudent(null);
        }}
        onSubmit={handleFormSubmit}
        student={selectedStudent}
        uploading={uploading}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Устгахыг баталгаажуулах</DialogTitle>
        <DialogContent>Энэ оюутныг устгахдаа итгэлтэй байна уу? Энэ үйлдлийг буцаах боломжгүй.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Цуцлах</Button>
          <Button onClick={handleDeleteConfirm} color='error' variant='contained'>
            Устгах
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;
